import { useTranslation } from 'react-i18next';
import { useFormatters } from '@/shared/hooks/useFormatters/useFormatters.ts';
import { SUPPORTED_UNIT_LABEL } from '@/phrase/SupportedUnitLabels.ts';
import { PRODUCT_NAME_LABEL } from '@/phrase/ProductNameLabels.ts';
import { BusinessCase } from '@schuettflix/interfaces';
import { ProductConfiguration } from '@/shared/models/ContextDataModel/interfaces/entities/product-configuration.ts';

type SummaryItem = { key: string; label: string; value: string };

type AutomaticProductsFormSummaryProps = {
    productName: string;
    businessCase: BusinessCase;
    productConfigurations: ProductConfiguration[];
};

export function AutomaticProductsFormSummary({
    productName,
    businessCase,
    productConfigurations,
}: AutomaticProductsFormSummaryProps) {
    const { t } = useTranslation();
    const { formatNumber } = useFormatters();

    const materialProduct = productConfigurations.find(p => p.category === 'MATERIAL');
    const transportProduct = productConfigurations.find(p => p.category === 'TRANSPORT');
    const additionalServices = productConfigurations.filter(
        p => p.category === 'MATERIAL_SERVICE' || p.category === 'TRANSPORT_SERVICE'
    );

    const salesItems: SummaryItem[] = [];
    const purchaseItems: SummaryItem[] = [];

    if (materialProduct?.salesQuantity && materialProduct.salesUnit) {
        salesItems.push({
            key: 'sales-material',
            label: productName,
            value: `${formatNumber(materialProduct.salesQuantity)} ${t(SUPPORTED_UNIT_LABEL[materialProduct.salesUnit])}`,
        });
    }
    if (transportProduct?.salesQuantity && transportProduct.salesUnit) {
        salesItems.push({
            key: 'sales-transport',
            label: t('forms.automaticProductsForm.sections.transport'),
            value: `${formatNumber(transportProduct.salesQuantity)} ${t(SUPPORTED_UNIT_LABEL[transportProduct.salesUnit])}`,
        });
    }
    if (materialProduct?.purchaseQuantity && materialProduct.purchaseUnit) {
        purchaseItems.push({
            key: 'purchase-material',
            label: productName,
            value: `${formatNumber(materialProduct.purchaseQuantity)} ${t(SUPPORTED_UNIT_LABEL[materialProduct.purchaseUnit])}`,
        });
    }
    if (transportProduct?.purchaseQuantity && transportProduct.purchaseUnit) {
        purchaseItems.push({
            key: 'purchase-transport',
            label: t('forms.automaticProductsForm.sections.transport'),
            value: `${formatNumber(transportProduct.purchaseQuantity)} ${t(SUPPORTED_UNIT_LABEL[transportProduct.purchaseUnit])}`,
        });
    }
    additionalServices.forEach(service => {
        salesItems.push({
            key: `sales-service-${service.index}`,
            label: service.productName,
            value: `${formatNumber(service.salesQuantity)} ${t(SUPPORTED_UNIT_LABEL[service.salesUnit])}`,
        });
        purchaseItems.push({
            key: `purchase-service-${service.index}`,
            label: service.productName,
            value: `${formatNumber(service.purchaseQuantity)} ${t(SUPPORTED_UNIT_LABEL[service.purchaseUnit])}`,
        });
    });

    return (
        <div className="flex flex-col gap-2">
            <div className="font-copy-md flex justify-between gap-2">
                <span className="text-subdued min-w-0 truncate">
                    {t('automaticProductConfigurationSummary.businessCaseName')}
                </span>
                <span className="min-w-0 truncate text-right">{t(PRODUCT_NAME_LABEL[businessCase.productType])}</span>
            </div>

            <hr className="border-light-gray-500" />

            {salesItems.length > 0 && (
                <SummarySection title={t('forms.automaticProductsForm.sections.salesPositions')} items={salesItems} />
            )}

            {salesItems.length > 0 && purchaseItems.length > 0 && <hr className="border-light-gray-500" />}

            {purchaseItems.length > 0 && (
                <SummarySection
                    title={t('forms.automaticProductsForm.sections.purchasePositions')}
                    items={purchaseItems}
                />
            )}
        </div>
    );
}

function SummarySection({ title, items }: { title: string; items: SummaryItem[] }) {
    return (
        <div className="flex flex-col gap-1">
            <span className="font-copy-sm-strong">{title}</span>
            {items.map(({ key, label, value }) => (
                <div key={key} className="font-copy-md flex justify-between gap-2">
                    <span className="text-subdued min-w-0 truncate">{label}</span>
                    <span className="min-w-0 truncate text-right">{value}</span>
                </div>
            ))}
        </div>
    );
}
